import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';

import { MenuItem } from 'primeng/api';
import { BehaviorSubject, Subscription } from 'rxjs';

import { CurationAgentSourceStatusEnum } from '../../../api/agents/models/curation-agent.api';
import { QuotaUsageRateEnum } from '../../../api/mediaSubscriptions/enums/quota-usage-rate.enum';
import { CustomPromptsManagementDialogComponent }
    from '../../../Admin/AccountManagement/components/CustomPromptsManagement/custom-prompts-management-dialog.component';
import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { SplitButtonCommandEvent } from '../../../Common/Forms/components/SplitButton/split-button.component';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { FullProfileDialogAnimationsEnum } from '../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import {
    CurationOutcomeEnum,
    curationOutcomeLabels,
    curationOutcomes
} from '../../enums/curation-outcome.enum';
import { curationPromptType } from '../../enums/curation-prompt-type.const';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { MonitoringSeriesTypeEnum } from '../../../Monitor/Shared/enums/monitoring-series-type.enum';
import { defaultSourceTypeIcon, sourceTypeIcons, sourceTypeLabels } from '../../enums/source-icons.const';
import { folderActionTypes, tagActionTypes } from '../../enums/curation-agent-action-type.enum';
import { CurationAgentsService, sourceNameKey } from '../../services/curation-agents.service';
import { CurationAgentActionModel } from '../../models/curation-agent-action.model';
import { CurationAgentModel } from '../../models/curation-agent.model';
import { CurationAgentSourceModel } from '../../models/curation-agent-source.model';

/** Quota usage indicator, per the Quota Usage Icons design. Shared with the Social theme card. */
const quotaUsageIcons: Record<QuotaUsageRateEnum, string> = {
    [QuotaUsageRateEnum.INACTIVE]: 'fa-regular fa-duotone fa-circle',
    [QuotaUsageRateEnum.SAFE_USAGE]: 'fa-regular fa-duotone fa-scrubber',
    [QuotaUsageRateEnum.HIGH_USAGE]: 'fa-kit fa-agpr-circle-high',
    [QuotaUsageRateEnum.EXCESSIVE_USAGE]: 'fa-solid fa-circle-exclamation'
};

/** Anything a click could have been meant for, rather than for the card behind it */
const interactiveSelector: string =
    'button, a, input, label, agpr-button, agpr-split-button, agpr-social-switch, agpr-checkbox';

/**
 * Mentions analyzed in a day above which a source counts as high, then excessive.
 * @TODO: confirm the real thresholds - they should most likely come from the account's quota rather
 * than being fixed here.
 */
const highUsagePerDay: number = 500;
const excessiveUsagePerDay: number = 1000;

/**
 * One agent in the management list. Collapsed it is a summary row; expanded it splits into the
 * agent's own details on the left and its sources on the right, following the Social theme card.
 */
@Component({
    selector: 'agpr-agent-card',
    templateUrl: './agent-card.component.html',
    styleUrls: ['./agent-card.component.scss'],
    animations: [
        trigger('containerState', [
            state('collapsed', style({ height: '50px'})),
            state('expanded', style({ height: '{{ cardHeight }}' }), { params: { cardHeight: '250px' } }),
            transition('* => collapsed', animate('220ms ease-out')),
            transition('* => expanded', animate('220ms ease-in'))
        ])
    ],
    standalone: false
})
export class AgentCardComponent implements OnDestroy {
    /** All five must stay in step with the card stylesheet, which the animation measures against */
    static readonly minExpandedHeight: number = 200;
    static readonly sourcesChrome: number = 86;
    static readonly sourceRowHeight: number = 44;
    /** How many rows at the foot of the list open their menu upwards instead of downwards */
    static readonly rowsBelowMenu: number = 2;

    readonly buttonTypes = ButtonTypesEnum;
    readonly outcomeList: CurationOutcomeEnum[] = curationOutcomes;
    readonly sourceTypeLabels = sourceTypeLabels;
    readonly sourceTypeIcons = sourceTypeIcons;
    readonly defaultSourceTypeIcon = defaultSourceTypeIcon;
    readonly sourceStatus = CurationAgentSourceStatusEnum;
    readonly sourceType = MonitoringSeriesTypeEnum;

    @Input() agent: CurationAgentModel;
    @Input() expanded: boolean = false;

    @Output() expandedChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() edited: EventEmitter<CurationAgentModel> = new EventEmitter<CurationAgentModel>();
    @Output() deleted: EventEmitter<CurationAgentModel> = new EventEmitter<CurationAgentModel>();
    @Output() viewed: EventEmitter<CurationAgentModel> = new EventEmitter<CurationAgentModel>();
    @Output() sourceToggled: EventEmitter<CurationAgentSourceModel> = new EventEmitter<CurationAgentSourceModel>();
    @Output() sourceViewed: EventEmitter<CurationAgentSourceModel> = new EventEmitter<CurationAgentSourceModel>();
    @Output() sourceRemoved: EventEmitter<CurationAgentSourceModel> = new EventEmitter<CurationAgentSourceModel>();

    // The split button hands its [data] to the command as event.data, not on the item
    readonly viewSourceOption: MenuItem = {
        label: this.localizationService.transform('AutomationAgents.card.source.view'),
        command: (event: SplitButtonCommandEvent) => this.sourceViewed.emit(event.data)
    };
    readonly removeSourceOption: MenuItem = {
        label: this.localizationService.transform('AutomationAgents.card.source.remove'),
        command: (event: SplitButtonCommandEvent) => this.sourceRemoved.emit(event.data)
    };

    private readonly subscriptions: Subscription[] = [];

    /** Resolved once from the service rather than per change detection - see sourceNames$ */
    private sourceNames: Map<string, string> = new Map<string, string>();
    private promptNames: Map<number, string> = new Map<number, string>();

    constructor(
        private localizationService: LocalizationService,
        private curationAgentsService: CurationAgentsService,
        private coreDialogService: CoreDialogService
    ) {
        this.subscriptions.push(
            this.curationAgentsService.sourceNames$.subscribe(names => this.sourceNames = names),
            this.curationAgentsService.promptNames$.subscribe(names => this.promptNames = names)
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.subscriptions.length = 0;
    }

    /** The curation prompt the agent judges mentions against, blank until the lookup has loaded */
    get promptName(): string {
        return this.promptNames.get(this.agent?.customPromptId) ?? '';
    }

    /** Opens the shared prompt manager, scoped to curation prompts */
    openPromptManager(): void {
        this.coreDialogService.open(CustomPromptsManagementDialogComponent, {
            inputs: {
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                ),
                overlayContainer: document.getElementById('main-content'),
                selectedPromptType: curationPromptType,
                // Opens on the agent's own prompt rather than the first of that type
                selectedInsightId: this.agent?.customPromptId ?? null
            }
        });
    }

    /** The agent's actions for one outcome - which list they sit in is what triggers them */
    actionsFor(outcome: CurationOutcomeEnum): CurationAgentActionModel[] {
        return outcome === CurationOutcomeEnum.matching
            ? this.agent?.positiveActions ?? []
            : this.agent?.negativeActions ?? [];
    }

    outcomeLabel(outcome: CurationOutcomeEnum): string {
        return curationOutcomeLabels[outcome];
    }

    /** The rows the list actually shows - a half filled source has nothing to put in one */
    get completeSources(): CurationAgentSourceModel[] {
        return this.agent?.sources?.filter(source => source.isComplete()) ?? [];
    }

    /**
     * Which way a row's menu opens. Down by default; the last rows of a long list open upwards so
     * the panel does not spill past the bottom of the card - but only once there are rows above it
     * to open over, since a short list has nothing up there but the header.
     */
    sourceMenuPositionY(rowIndex: number): 'top' | 'bottom' {
        const rows = this.completeSources.length;

        return rows > AgentCardComponent.rowsBelowMenu && rowIndex >= rows - AgentCardComponent.rowsBelowMenu
            ? 'top'
            : 'bottom';
    }

    /** Sources the agent is actively pulling from, which is what the collapsed row counts */
    get activeSourceCount(): number {
        return this.agent.activeSources.length;
    }

    get totalSourceCount(): number {
        return this.agent.sources.length;
    }

    /**
     * Height the card animates to when opened
     */
    get cardHeight(): string {
        // Agent header height (50) + padding (20 top  + 20 bottom) + prompt label height (21) + gap (15) = 126
        const actionsSectionStaticHeight = 126;
        // Label height (21) + gap (10) = 31
        const actionHeaderHeight = 31;
        // Chip height (31) + gap (10) = 41
        const actionChipHeight = 41;
        let leftSideHeight = actionsSectionStaticHeight;

        if (this.agent?.positiveActions.length > 0) {
            leftSideHeight += actionHeaderHeight + (actionChipHeight * this.agent.positiveActions.length);
        }
        if (this.agent?.negativeActions.length > 0) {
            leftSideHeight += actionHeaderHeight + (actionChipHeight * this.agent.negativeActions.length);
        }

        const rows = this.agent?.sources?.filter(source => source.isComplete()).length ?? 0;
        const sourcesHeight = AgentCardComponent.sourcesChrome + (rows * AgentCardComponent.sourceRowHeight);

        const height = Math.max(AgentCardComponent.minExpandedHeight, leftSideHeight, sourcesHeight);

        return `${height}px`;
    }

    toggleCard(): void {
        this.expandedChange.emit(!this.expanded);
    }

    /**
     * The card as a whole opens the agent's dashboard, but it is full of its own controls - the
     * expand toggle, the row switches, the context menus - so anything that came from one of those
     * is left to handle itself.
     */
    cardClicked(event: MouseEvent): void {
        if (this.expanded) {
            return;
        }
        if ((event.target as HTMLElement)?.closest(interactiveSelector)) {
            return;
        }

        this.viewed.emit(this.agent);
    }

    /**
     * The source's display name, falling back to the raw reference until the lookup has loaded.
     * Returns a plain string: the template calls this on every change detection, so it must not
     * build a new observable each time.
     */
    sourceName(source: CurationAgentSourceModel): string {
        const id = source.dataSource?.id;
        const fallback = source.dataSource?.dataSourceName ?? String(id ?? '');

        return this.sourceNames.get(sourceNameKey(source.dataSource?.type, id)) ?? fallback;
    }

    /**
     * How hard the source is drawing on the account's quota, which drives the indicator beside it.
     * Inactive is the source's own status and nothing else - one that is running but has not been
     * scanned yet is drawing nothing, which is safe usage rather than inactive.
     */
    quotaUsage(source: CurationAgentSourceModel): QuotaUsageRateEnum {
        if (!source.isActive) {
            return QuotaUsageRateEnum.INACTIVE;
        }

        if (source.analyzedLast24h >= excessiveUsagePerDay) {
            return QuotaUsageRateEnum.EXCESSIVE_USAGE;
        }

        return source.analyzedLast24h >= highUsagePerDay
            ? QuotaUsageRateEnum.HIGH_USAGE
            : QuotaUsageRateEnum.SAFE_USAGE;
    }

    /** A failing source shows the error state rather than whatever it last drew from the quota */
    quotaUsageIcon(source: CurationAgentSourceModel): string {
        return source.hasError
            ? quotaUsageIcons[QuotaUsageRateEnum.EXCESSIVE_USAGE]
            : quotaUsageIcons[this.quotaUsage(source)];
    }

    quotaUsageTooltip(source: CurationAgentSourceModel): string {
        return source.hasError
            ? 'AutomationAgents.agent.source.error'
            : `AutomationAgents.card.usage.${this.quotaUsage(source)}`;
    }

    statusChanged(source: CurationAgentSourceModel, active: boolean): void {
        source.status = active ? CurationAgentSourceStatusEnum.active : CurationAgentSourceStatusEnum.paused;
        this.sourceToggled.emit(source);
    }

    /** Only the actions that target coverage have somewhere to open */
    canViewActionTarget(action: CurationAgentActionModel): boolean {
        return !!action.referenceIds.length
            && (folderActionTypes.includes(action.type) || tagActionTypes.includes(action.type));
    }

    /** Opens the folder or tag the action saves to - whichever of its targets was clicked - in a new tab */
    viewActionTarget(action: CurationAgentActionModel, referenceId: string): void {
        this.curationAgentsService.viewActionTarget(action, referenceId);
    }

    actionChipIcon(action: CurationAgentActionModel): string {
        return action.icon;
    }

    sourceOptions(source: CurationAgentSourceModel) {
        const options: MenuItem[] = [
            this.removeSourceOption
        ];

        if (source.dataSource.type !== MonitoringSeriesTypeEnum.socialTopic) {
            options.unshift(this.viewSourceOption);
        }

        return options;
    }
}
